import { Checkbox, Chip, ListItemIcon, ListItemText } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useCallback, useMemo } from 'react';
import { baseFormInlineClassNames } from '../../../theme/component-styles';
import { IFormFieldSelectMeta } from '../../../view-models/form.view-model';
import { MoreChipWithTooltip } from '../../../pages/CompanyProfiles/Summary/components/MoreChipWithTooltip/MoreChipWithTooltip';
import { Autocomplete } from '../../Autocomplete/Autocomplete';
import { SelectType, useFieldSelect } from './FieldSelect';
import { IBaseFieldProps } from './types';
import { addOptionLabel, useFreeSoloEntry } from './useFreeSoloEntry';

// Rounds saved before typed multi-select storage hold comma-joined strings in customData;
// MUI's Autocomplete requires an array, so a raw string would crash the whole form.
function normalizeMultiSelectValue(raw: unknown): SelectType[] {
  if (Array.isArray(raw)) {
    return raw as SelectType[];
  }
  if (typeof raw === 'string') {
    return raw
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);
  }
  return raw == null ? [] : [raw as SelectType];
}

export function FieldMultiSelect(props: IBaseFieldProps<IFormFieldSelectMeta<SelectType>>) {
  const { formField, formProps } = props;
  const { ref, onChange, onBlur, value: rawValue } = formProps;
  const value = useMemo(() => normalizeMultiSelectValue(rawValue), [rawValue]);
  const { disabled = false, label, variant, placeholder } = formField ?? {};
  const { allowCustomAdd, compactOptions, endAdornment, inlinePopper, onCustomAdd } =
    formField.rendererMeta ?? {};
  const className = variant === 'form-inline' ? `${baseFormInlineClassNames} form-inline-multiselect` : '';

  const effectiveFormField = useMemo(() => {
    if (
      allowCustomAdd &&
      !formField.rendererMeta?.values?.length &&
      Array.isArray(value) &&
      value.length > 0
    ) {
      return {
        ...formField,
        rendererMeta: {
          ...formField.rendererMeta,
          values: (value as unknown[]).map((v) => ({ displayName: String(v), value: String(v) })),
        },
      };
    }
    return formField;
  }, [allowCustomAdd, formField, value]);

  const { getOptionsLabel, options, valuesAsMap, getOptionDisabled } = useFieldSelect(effectiveFormField);

  const handleChange = useCallback(
    (values: SelectType[]) => {
      onChange(values);
      if (allowCustomAdd && onCustomAdd) {
        const newValues = values.filter((v) => !valuesAsMap.has(v));
        if (newValues.length) {
          newValues.forEach((v) => onCustomAdd(String(v)));
        }
      }
    },
    [allowCustomAdd, onChange, onCustomAdd, valuesAsMap]
  );

  const { commitPending, filterOptions, inputValue, isAddOption, onInputChange } =
    useFreeSoloEntry<SelectType>({
      enabled: !!allowCustomAdd,
      value,
      options,
      onChange: handleChange,
      getOptionLabel: getOptionsLabel,
    });

  const resolve = (option: SelectType) =>
    valuesAsMap.get(option) ??
    (allowCustomAdd ? { displayName: String(option), value: option, secondaryLabel: undefined } : undefined);

  const autocomplete = (
    <Autocomplete<SelectType, true>
      ref={ref}
      style={{ pointerEvents: 'auto' }} // otherwise hovering over the more chip won't show other values
      disabled={disabled}
      aria-label={label}
      className={className}
      disableCloseOnSelect
      filterOptions={(opts, text) =>
        filterOptions(opts, { inputValue: text, getOptionLabel: getOptionsLabel })
      }
      getOptionLabel={getOptionsLabel}
      freeSolo={allowCustomAdd}
      inputValue={inputValue}
      multiple
      onBlur={() => {
        // Typed text left in the box on click out / tab out becomes a tag rather than being lost.
        commitPending();
        onBlur?.();
      }}
      onChange={handleChange}
      onInputChange={onInputChange}
      openOnFocus
      options={options}
      // Opt-in per field: in flow the dropdown scrolls with its input rather than being left
      // floating; only the caller knows whether an in-flow popper has a clipping ancestor.
      disablePortal={inlinePopper}
      placeholder={placeholder}
      onKeyDown={(event) => {
        // Prevent the backspace deletes tags behaviour.
        if (event.currentTarget.value.length === 0 && event.key === 'Backspace') {
          event.stopPropagation();
        }
      }}
      renderOption={(option, { selected, inputValue: text }) => {
        if (isAddOption(option, { inputValue: text })) {
          return (
            <>
              <ListItemIcon>
                <AddIcon fontSize='small' />
              </ListItemIcon>
              <ListItemText primary={addOptionLabel(option)} />
            </>
          );
        }
        const resolved = resolve(option);
        if (!resolved) {
          return null;
        }
        const { displayName, value: optionValue, secondaryLabel = undefined } = resolved;
        return (
          <>
            <Checkbox
              checked={selected}
              size={compactOptions ? 'small' : 'medium'}
              sx={compactOptions ? { p: 0, mr: '8px' } : undefined}
            />
            <ListItemText
              primary={displayName ?? optionValue}
              secondary={secondaryLabel}
              sx={compactOptions ? { m: 0 } : undefined}
            />
          </>
        );
      }}
      renderTags={(picks) => {
        if (!picks?.length) {
          return <></>;
        }

        const [first, ...others] = picks.map((option) => {
          const item = resolve(option);
          return item ? (item.displayName ?? item.secondaryLabel ?? item.value.toString()) : '';
        });

        if (picks.length === 1) {
          return <Chip size='small' label={first} />;
        }
        return (
          <>
            <Chip size='small' label={first} />
            <MoreChipWithTooltip
              // Not "Other": in a list of named options that reads as one more option.
              title={`Also selected (${others.length})`}
              moreChipTotal={others.length}
              otherItems={others}
            ></MoreChipWithTooltip>
          </>
        );
      }}
      getOptionDisabled={getOptionDisabled}
      value={value}
    />
  );

  if (!endAdornment) {
    return autocomplete;
  }

  // Beside the input, not inside it: the adornment stays clear of the field's border and hover
  // controls, and its clicks cannot open the dropdown. `minWidth: 0` lets the input shrink so the
  // adornment never wraps.
  return (
    <div style={{ alignItems: 'center', display: 'flex', gap: '0.25rem' }}>
      <div style={{ flexGrow: 1, minWidth: 0 }}>{autocomplete}</div>
      <span style={{ flexShrink: 0 }}>{endAdornment}</span>
    </div>
  );
}
